import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Box,
  Grid,
  Typography,
  Avatar,
  Rating,
  Chip,
  Paper,
  Divider,
  Button,
  CircularProgress,
  Alert,
  Skeleton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormHelperText,
  Tabs,
  Tab
} from '@mui/material';
import FortuneTellerReviews from '../components/fortune/FortuneTellerReviews';
import { styled } from '@mui/material/styles';
import StarIcon from '@mui/icons-material/Star';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import SchoolIcon from '@mui/icons-material/School';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaymentIcon from '@mui/icons-material/Payment';
import { collection, addDoc, serverTimestamp, arrayUnion, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { getFortuneTellerById, createBooking } from '../services/fortuneTellerService';
import TimeSlotSelector from '../components/booking/TimeSlotSelector';
import FortuneServerPayButton from '../components/fortune/FortuneServerPayButton';
import FortunePayPalWrapper from '../components/fortune/FortunePayPalWrapper';

// 样式化组件
const DetailCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  borderRadius: theme.shape.borderRadius,
}));

const SpecialtyChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  backgroundColor: 'rgba(156, 39, 176, 0.1)',
  borderColor: theme.palette.primary.light,
  '& .MuiChip-label': {
    color: theme.palette.primary.main
  }
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 150,
  height: 150,
  boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
  [theme.breakpoints.down('sm')]: {
    width: 120,
    height: 120,
  }
}));

const StyledRating = styled(Rating)(({ theme }) => ({
  '& .MuiRating-iconFilled': {
    color: theme.palette.primary.main,
  }
}));

// 占卜师详情页面
const FortuneTellerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  
  const [fortuneTeller, setFortuneTeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bookingDialog, setBookingDialog] = useState(false);
  const [bookingNote, setBookingNote] = useState('');
  const [selectedTimeSlots, setSelectedTimeSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [bookingError, setBookingError] = useState('');
  const [bookingInProgress, setBookingInProgress] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingID, setBookingID] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [paymentStep, setPaymentStep] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [networkStatus, setNetworkStatus] = useState('online');
  
  // 获取占卜师详情
  useEffect(() => {
    const fetchFortuneTeller = async () => {
      console.log(`开始获取占卜师ID=${id}的详情`);
      setLoading(true);
      setError('');
      
      try {
        if (!id) {
          throw new Error('无效的占卜师ID');
        }
        
        // 检查网络状态
        if (!navigator.onLine) {
          console.warn('网络连接已断开，可能影响数据获取');
          setNetworkStatus('offline');
        } else {
          setNetworkStatus('online');
        }
        
        // 从Firestore获取单个占卜师详情
        const data = await getFortuneTellerById(id);
        console.log('成功获取占卜师详情:', data);
        
        if (!data) {
          throw new Error('未找到占卜师数据');
        }
        
        // 确保数据包含所有必要字段（可能的类型修复）
        const processedData = {
          id: data.id,
          name: data.name || '未命名占卜师',
          title: data.title || '占卜师',
          photoURL: data.photoURL || 'https://storage.googleapis.com/destinycrystal-ff557.firebasestorage.app/placeholders/avatar-default.jpg',
          rating: data.rating || 0,
          reviewCount: data.reviewCount || 0,
          price: data.price || 0,
          specialties: Array.isArray(data.specialties) ? data.specialties : [],
          featured: data.featured || false,
          experience: data.experience || 0,
          bio: data.bio || '暂无简介',
          credentials: data.credentials || '专业占卜师',
          availability: data.availability || {
            monday: [], tuesday: [], wednesday: [], thursday: [],
            friday: [], saturday: [], sunday: []
          },
          // 保留其他原始数据，确保不丢失任何字段
          ...data
        };
        
        setFortuneTeller(processedData);
        
        // 检查是否收藏
        if (userData && userData.favoriteFortuneTellers) {
          const isFavorited = Array.isArray(userData.favoriteFortuneTellers) && 
                             userData.favoriteFortuneTellers.includes(id);
          console.log(`占卜师收藏状态: ${isFavorited ? '已收藏' : '未收藏'}`);
          setIsFavorite(isFavorited);
        }
      } catch (error) {
        console.error('获取占卜师详情失败:', error);
        console.error('错误详情:', error.code, error.message);
        
        // 检查是否为网络错误
        const isNetworkError = 
          error.code === 'NETWORK_ERROR' || 
          error.code === 'unavailable' || 
          error.code === 'functions/unavailable' ||
          error.message?.includes('network') || 
          error.message?.includes('timeout') ||
          error.message?.includes('CORS') ||
          !navigator.onLine;
        
        if (isNetworkError) {
          setNetworkStatus('offline');
          setError('网络连接问题，无法获取数据。请检查您的网络连接后重试。');
        } else if (error.message === '占卜师不存在' || error.message === '未找到占卜师数据') {
          setError('该占卜师不存在或已被删除');
        } else if (error.message === '无效的占卜师ID') {
          setError('请提供有效的占卜师ID');
        } else {
          setError('获取占卜师信息失败，请稍后重试');
        }
      } finally {
        setLoading(false);
        console.log('占卜师详情数据获取完成');
      }
    };
    
    if (id) {
      fetchFortuneTeller();
    } else {
      setError('未提供占卜师ID');
      setLoading(false);
    }
  }, [id, userData]);
  
  // 监听网络状态变化
  useEffect(() => {
    // 网络状态变化处理函数
    const handleNetworkChange = () => {
      const isOnline = navigator.onLine;
      console.log(`网络状态变化: ${isOnline ? 'online' : 'offline'}`);
      setNetworkStatus(isOnline ? 'online' : 'offline');
      
      // 如果恢复在线状态，并且之前有错误或者没有获取到数据，显示重试提示
      if (isOnline && (error || !fortuneTeller) && id) {
        console.log('网络恢复，可以重新获取占卜师详情');
        // 不自动重新加载，而是显示提示，让用户点击重试按钮
        if (error && error.includes('网络连接问题')) {
          setError('网络已恢复，请刷新页面重试');
        }
      }
    };

    // 添加事件监听
    window.addEventListener('online', handleNetworkChange);
    window.addEventListener('offline', handleNetworkChange);

    // 清理函数
    return () => {
      window.removeEventListener('online', handleNetworkChange);
      window.removeEventListener('offline', handleNetworkChange);
    };
  }, [error, fortuneTeller, id]);
  
  // 处理预约按钮点击
  const handleBookingClick = () => {
    // 检查是否已登录
    if (!currentUser) {
      // 提示用户登录
      navigate('/login', { state: { from: `/fortune-teller/${id}` } });
      return;
    }
    
    setBookingDialog(true);
  };
  
  // 处理时间段选择
  const handleTimeSlotSelect = (timeSlots, date) => {
    setSelectedTimeSlots(timeSlots);
    setSelectedDate(date);
  };
  
  // 提交预约
  const handleBookingSubmit = async () => {
    // 表单验证
    if (!selectedDate) {
      setBookingError('请选择预约日期');
      return;
    }
    
    if (selectedTimeSlots.length === 0) {
      setBookingError('请选择至少一个预约时间段');
      return;
    }
    
    // 再次检查是否已登录
    if (!currentUser) {
      setBookingError('请先登录后再预约');
      return;
    }
    
    setBookingInProgress(true);
    setBookingError('');
    
    try {
      // 计算总价
      const totalPrice = fortuneTeller.price * selectedTimeSlots.length;
      setPaymentAmount(totalPrice);
      
      // 准备预约数据
      const bookingData = {
        userId: currentUser.uid,
        userName: userData?.displayName || currentUser.displayName || '未知用户',
        fortuneTellerId: fortuneTeller.id,
        fortuneTellerName: fortuneTeller.name,
        date: selectedDate,
        formattedDate: selectedDate.toISOString().split('T')[0],
        timeSlots: selectedTimeSlots, // 新的多时间段数据格式
        notes: bookingNote,
        price: totalPrice, // 价格乘以时间段数量
        status: 'pending', // 待支付状态
        paymentStatus: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      // 添加到 Firestore
      const bookingId = await createBooking(bookingData);
      setBookingID(bookingId);
      setBookingSuccess(true);
      
      // 重定向到预约结账页面，而不是在对话框中显示支付按钮
      navigate('/fortune-booking-checkout', {
        state: {
          bookingData,
          fortuneTeller,
          bookingId
        }
      });
      
      // 关闭当前对话框
      setBookingDialog(false);
    } catch (error) {
      console.error('创建预约失败:', error);
      setBookingError('预约失败，请稍后重试');
    } finally {
      setBookingInProgress(false);
    }
  };
  
  // 处理支付成功
  const handlePaymentSuccess = async (paymentDetails) => {
    console.log('支付成功:', paymentDetails);
    setPaymentSuccess(true);
    
    // 注意：当使用服务器端PayPal时，支付成功后预约状态已经在FortuneServerPayButton组件中更新
    // 所以这里不需要再次更新，仅保留以兼容旧版流程
    try {
      // 检查是否是服务器端流程更新的支付
      const isServerPayment = paymentDetails && paymentDetails.captureDetails;
      
      // 如果不是服务器端支付成功回调，则手动更新预约状态
      if (!isServerPayment && bookingID) {
        // 更新预约文档
        const bookingRef = doc(db, 'bookings', bookingID);
        await updateDoc(bookingRef, {
          status: 'confirmed',
          paymentStatus: 'completed',
          paymentDetails: paymentDetails,
          updatedAt: serverTimestamp()
        });
        console.log('预约状态已更新为已支付');
      }
    } catch (error) {
      console.error('更新支付状态失败:', error);
      // 虽然更新失败，但支付已成功，所以不设置错误状态
    }
  };
  
  // 处理支付错误
  const handlePaymentError = (error) => {
    console.error('支付失败:', error);
    setPaymentError(`支付处理出错: ${error.message || '未知错误'}`);
  };
  
  // 添加到收藏
  const handleToggleFavorite = async () => {
    if (!currentUser) {
      navigate('/login', { state: { from: `/fortune-teller/${id}` } });
      return;
    }
    
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      
      if (isFavorite) {
        // 从收藏中移除
        await updateDoc(userRef, {
          favoriteFortuneTellers: userData.favoriteFortuneTellers.filter(tellerId => tellerId !== id)
        });
      } else {
        // 添加到收藏
        await updateDoc(userRef, {
          favoriteFortuneTellers: arrayUnion(id)
        });
      }
      
      // 更新本地状态
      setIsFavorite(!isFavorite);
    } catch (error) {
      console.error('更新收藏失败:', error);
    }
  };
  
  // Tab切换
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // 关闭预约对话框
  const handleCloseDialog = () => {
    if (bookingSuccess) {
      // 如果预约成功，可以导航到用户中心或其他页面
      navigate('/user');
    } else {
      setBookingDialog(false);
    }
  };
  
  // 渲染骨架屏
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Skeleton variant="circular" width={150} height={150} />
            </Box>
            <Skeleton variant="text" height={60} />
            <Skeleton variant="text" />
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <Skeleton variant="rectangular" width={200} height={40} />
            </Box>
          </Grid>
          <Grid item xs={12} md={8}>
            <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
            <Skeleton variant="text" />
            <Skeleton variant="text" />
            <Skeleton variant="text" />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', my: 2 }}>
              {[1, 2, 3, 4].map(i => (
                <Skeleton key={i} variant="rectangular" width={80} height={32} sx={{ mr: 1, mb: 1 }} />
              ))}
            </Box>
          </Grid>
        </Grid>
      </Container>
    );
  }
  
  // 渲染错误提示
  if (error) {
    const isNetworkError = 
      error.includes('网络连接问题') || 
      error.includes('网络已恢复') || 
      networkStatus === 'offline';
      
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert 
          severity={isNetworkError ? "warning" : "error"} 
          sx={{ maxWidth: 600, mx: 'auto', mb: 2 }}
        >
          {error}
        </Alert>
        
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          {isNetworkError && (
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => window.location.reload()}
              sx={{ mr: 2 }}
            >
              重新加载页面
            </Button>
          )}
          
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={() => navigate('/fortune-teller')}
          >
            返回占卜师列表
          </Button>
        </Box>
        
        {/* 网络状态提示 - 仅在断网时显示 */}
        {networkStatus === 'offline' && (
          <Alert severity="info" sx={{ maxWidth: 600, mx: 'auto', mt: 3 }}>
            检测到您目前处于离线状态，请检查网络连接后重试。
          </Alert>
        )}
      </Container>
    );
  }
  
  // 渲染占卜师未找到
  if (!fortuneTeller) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="warning" sx={{ maxWidth: 600, mx: 'auto', mb: 2 }}>
          未找到该占卜师信息
        </Alert>
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={() => navigate('/fortune-teller')}
          >
            返回占卜师列表
          </Button>
        </Box>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <DetailCard>
        <Grid container spacing={4}>
          {/* 左侧: 头像和基本信息 */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <StyledAvatar 
                src={fortuneTeller.photoURL} 
                alt={fortuneTeller.name}
              />
              
              <Typography variant="h4" align="center" sx={{ mt: 2 }}>
                {fortuneTeller.name}
              </Typography>
              
              <Typography variant="subtitle1" color="text.secondary" align="center">
                {fortuneTeller.title}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', my: 1 }}>
                <StyledRating 
                  value={fortuneTeller.rating} 
                  precision={0.1} 
                  readOnly 
                  emptyIcon={<StarIcon style={{ opacity: 0.55 }} fontSize="inherit" />}
                />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  {fortuneTeller.rating} ({fortuneTeller.reviewCount}条评价)
                </Typography>
              </Box>
              
              <Typography variant="h5" color="primary" align="center" sx={{ my: 1 }}>
              S${fortuneTeller.price} <Typography component="span" variant="body2">/次</Typography>
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<EventAvailableIcon />}
                  onClick={handleBookingClick}
                >
                  立即预约
                </Button>
                
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={isFavorite ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                  onClick={handleToggleFavorite}
                >
                  {isFavorite ? '已收藏' : '收藏'}
                </Button>
              </Box>
            </Box>
          </Grid>
          
          {/* 右侧: 详细信息 */}
          <Grid item xs={12} md={8}>
            <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
              <Tab label="简介" />
              <Tab label="专长" />
              <Tab label="评价" />
            </Tabs>
            
            {/* 简介 Tab */}
            {tabValue === 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  个人简介
                </Typography>
                <Typography paragraph>
                  {fortuneTeller.bio}
                </Typography>
                
                <Divider sx={{ my: 2 }} />
                
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <WorkHistoryIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="执业年限" 
                      secondary={`${fortuneTeller.experience}年`} 
                    />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemIcon>
                      <SchoolIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="专业资质" 
                      secondary={fortuneTeller.credentials || "国际占星师协会认证占星师"} 
                    />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemIcon>
                      <MonetizationOnIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="咨询价格" 
                      secondary={`S${fortuneTeller.price} (每30分钟)`} 
                    />
                  </ListItem>
                </List>
              </Box>
            )}
            
            {/* 专长 Tab */}
            {tabValue === 1 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  专长领域
                </Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 2 }}>
                  {Array.isArray(fortuneTeller.specialties) && fortuneTeller.specialties.length > 0 
                    ? fortuneTeller.specialties.map(specialty => (
                        <SpecialtyChip 
                          key={specialty} 
                          label={specialty} 
                          variant="outlined" 
                        />
                      ))
                    : <Typography variant="body2" color="text.secondary">暂无专长信息</Typography>
                  }
                </Box>
                
                <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                  可咨询问题
                </Typography>
                
                <Grid container spacing={2}>
                  {[
                    "事业发展与职业选择",
                    "感情关系与婚姻",
                    "财运与投资",
                    "家庭与人际关系",
                    "性格分析与潜能开发",
                    "运势预测",
                    "择日与风水",
                    "八字命盘详解"
                  ].map((item, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CheckCircleIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
                        <Typography variant="body2">{item}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
            
            {/* 评价 Tab */}
            {tabValue === 2 && (
              <Box>
                <FortuneTellerReviews fortuneTellerId={id} />
              </Box>
            )}
          </Grid>
        </Grid>
      </DetailCard>
      
      {/* 预约日历组件 */}
      <DetailCard>
        <Typography variant="h5" gutterBottom>
          查看可预约时间
        </Typography>
        <Typography variant="body2" paragraph color="text.secondary">
          请选择日期查看可预约的时间段。每个时间段为30分钟，预约连续的多个时间段可获得更长的咨询时间。
        </Typography>
        
        <TimeSlotSelector 
          fortuneTellerId={id} 
          onTimeSlotSelect={handleTimeSlotSelect} 
        />
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<EventAvailableIcon />}
            onClick={handleBookingClick}
            disabled={selectedTimeSlots.length === 0}
          >
            使用所选时间段预约
          </Button>
        </Box>
      </DetailCard>
      
      {/* 预约对话框 */}
      <Dialog
        open={bookingDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {paymentStep 
            ? `支付预约 - ${fortuneTeller?.name} 的占卜服务` 
            : `预约 ${fortuneTeller?.name} 的占卜服务`}
        </DialogTitle>
        <DialogContent>
          {paymentSuccess ? (
            // 支付成功状态
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                预约已确认并完成支付！
              </Typography>
              <Typography variant="body1" paragraph>
                您已成功预约并支付 {fortuneTeller.name} 的占卜咨询服务
              </Typography>
              
              <Paper elevation={1} sx={{ p: 3, maxWidth: 500, mx: 'auto', mb: 3, textAlign: 'left' }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  预约详情：
                </Typography>
                <Typography variant="body2">
                  预约时间：{selectedDate?.toLocaleDateString()} 
                  {selectedTimeSlots.length > 0 && (
                    <>（{selectedTimeSlots.join(', ')}）</>
                  )}
                </Typography>
                <Typography variant="body2">
                  预约时长：{selectedTimeSlots.length * 30}分钟
                </Typography>
                <Typography variant="body2">
                  咨询费用：S${paymentAmount}（已支付）
                </Typography>
                <Typography variant="body2">
                  预约编号：{bookingID}
                </Typography>
                <Typography variant="body2">
                  支付方式：PayPal
                </Typography>
                <Typography variant="body2">
                  支付状态：已完成
                </Typography>
              </Paper>
              
              <Alert severity="info" sx={{ mb: 2, maxWidth: 500, mx: 'auto', textAlign: 'left' }}>
                <Typography variant="body2">
                  请记录您的预约编号。占卜师将在预约时间前通过您的联系方式与您取得联系。您也可以在个人中心查看预约详情。
                </Typography>
              </Alert>
              
              <Typography variant="body2" color="text.secondary">
                支付收据将发送到您的邮箱和PayPal账户
              </Typography>
            </Box>
          ) : bookingSuccess && paymentStep ? (
            // 预约成功，显示支付页面
            <Box sx={{ py: 3 }}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  预约创建成功，请完成支付
                </Typography>
                
                <Paper sx={{ p: 3, mb: 3, maxWidth: 500, mx: 'auto', mt: 3 }}>
                  <Box sx={{ textAlign: 'left', mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      预约详情：
                    </Typography>
                    <Typography variant="body2">
                      预约编号：{bookingID}
                    </Typography>
                    <Typography variant="body2">
                      命理师：{fortuneTeller.name}
                    </Typography>
                    <Typography variant="body2">
                      日期：{selectedDate?.toLocaleDateString()}
                    </Typography>
                    <Typography variant="body2">
                      时间：{selectedTimeSlots.join(', ')}
                    </Typography>
                    <Typography variant="body2">
                      时长：{selectedTimeSlots.length * 30}分钟
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ mt: 1 }}>
                      应付金额：S${paymentAmount}
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="subtitle1" gutterBottom textAlign="center">
                    选择支付方式
                  </Typography>
                  
                  {paymentError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {paymentError}
                    </Alert>
                  )}
                  
                  <Box sx={{ mt: 2 }}>
                    <FortuneServerPayButton 
                      bookingId={bookingID}
                      amount={paymentAmount}
                      onSuccess={handlePaymentSuccess}
                      onErrorCallback={handlePaymentError}
                    />
                  </Box>
                </Paper>
              </Box>
            </Box>
          ) : (
            // 选择预约时间页面
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TimeSlotSelector 
                  fortuneTellerId={id}
                  onTimeSlotSelect={handleTimeSlotSelect}
                  initialDate={selectedDate}
                  initialSelectedTimeSlots={selectedTimeSlots}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  预约详情
                </Typography>
                
                {bookingError && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {bookingError}
                  </Alert>
                )}
                
                <Paper sx={{ p: 3, mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar
                      src={fortuneTeller.photoURL}
                      alt={fortuneTeller.name}
                      sx={{ width: 60, height: 60, mr: 2 }}
                    />
                    <Box>
                      <Typography variant="h6">
                        {fortuneTeller.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {fortuneTeller.title} | {fortuneTeller.experience}年经验
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <Rating 
                          value={fortuneTeller.rating} 
                          precision={0.1} 
                          readOnly 
                          size="small" 
                        />
                        <Typography variant="caption" sx={{ ml: 1 }}>
                          {fortuneTeller.rating}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="subtitle1" gutterBottom>
                    已选时间段
                  </Typography>
                  
                  {selectedTimeSlots.length > 0 ? (
                    <>
                      <Box>
                        <Typography variant="body2">
                          日期：{selectedDate?.toLocaleDateString() || '未选择'}
                        </Typography>
                        <Typography variant="body2">
                          时间：{selectedTimeSlots.join(', ')}
                        </Typography>
                        <Typography variant="body2">
                          时长：{selectedTimeSlots.length * 30}分钟
                        </Typography>
                      </Box>
                      
                      <Typography variant="h6" color="primary" sx={{ mt: 2 }}>
                        总价：S${fortuneTeller.price * selectedTimeSlots.length}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      请在左侧选择时间段
                    </Typography>
                  )}
                </Paper>
                
                <TextField
                  label="备注信息（可选）"
                  multiline
                  rows={4}
                  fullWidth
                  margin="normal"
                  value={bookingNote}
                  onChange={(e) => setBookingNote(e.target.value)}
                  placeholder="请告诉占卜师您想咨询的问题或关注点，以便提前准备"
                />
                
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    确认预约后，请立即完成支付以确保您的预约被保留。支付完成后，占卜师将在预约时间与您联系。
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {paymentSuccess ? (
            <Button onClick={handleCloseDialog} color="primary">
              完成
            </Button>
          ) : bookingSuccess && paymentStep ? (
            <>
              <Button 
                onClick={handleCloseDialog} 
                color="inherit"
                startIcon={<PaymentIcon />}
              >
                稍后支付
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleCloseDialog} color="inherit">
                取消
              </Button>
              <Button 
                onClick={handleBookingSubmit} 
                color="primary"
                variant="contained"
                disabled={selectedTimeSlots.length === 0 || bookingInProgress}
              >
                {bookingInProgress ? <CircularProgress size={24} /> : '确认预约并支付'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FortuneTellerDetail;